// Contributor activity cards - the per-person heatmap / bar cards and the
// team stacked bars that the Contributors page introduced, shared so the
// Analytics & Performance page renders contributor activity in the same
// style. `unit` names what is being counted ("commits", "events").
import React from "react";
import { Empty } from "antd";

export const COLORS = ["#3B82F6", "#1F2937", "#F59E0B", "#10B981", "#8B5CF6", "#EC4899"];
export function colorFor(idx) { return COLORS[idx % COLORS.length]; }

// === Per-user bar histogram (sparse - one bar per day) ===
export function BarHistogram({ daily, color, maxValue, height = 80 }) {
  const max = Math.max(1, maxValue || 1);
  return (
    <svg viewBox={`0 0 ${Math.max(1, daily.length)} 30`} preserveAspectRatio="none"
         style={{ width: "100%", height, display: "block" }}>
      {daily.map((d, i) => {
        const h = (d.count / max) * 30;
        return (
          <rect key={d.date} x={i + 0.1} y={30 - h}
                width={0.8} height={h} fill={color} opacity={0.85}>
            <title>{`${d.date}: ${d.count}`}</title>
          </rect>
        );
      })}
    </svg>
  );
}

export function Heatmap({ daily, color, maxValue }) {
  const byDate = new Map(daily.map(d => [d.date, d.count]));
  const today = new Date();
  const earliestStr = daily[0]?.date;
  let earliest = earliestStr ? new Date(earliestStr + "T00:00:00") : today;
  const dayOfWeek = (earliest.getDay() + 6) % 7;
  earliest.setDate(earliest.getDate() - dayOfWeek);
  const totalDays = Math.ceil((today - earliest) / (24 * 3600 * 1000)) + 1;
  const weeks = Math.ceil(totalDays / 7);

  const cellSize = 12, gap = 2;
  const w = weeks * (cellSize + gap), h = 7 * (cellSize + gap);
  const max = Math.max(1, maxValue || 1);
  const shade = (count) => {
    if (!count) return "#EFEFEF";
    const r = count / max;
    const buckets = [0.25, 0.5, 0.75, 1];
    const ix = buckets.findIndex(b => r <= b);
    const op = (ix === -1 ? 4 : ix + 1) * 0.22 + 0.1;
    return `${color}${Math.round(op * 255).toString(16).padStart(2, "0")}`;
  };

  const cells = [];
  for (let w_i = 0; w_i < weeks; w_i++) {
    for (let d_i = 0; d_i < 7; d_i++) {
      const date = new Date(earliest);
      date.setDate(earliest.getDate() + w_i * 7 + d_i);
      if (date > today) continue;
      const ds = date.toISOString().slice(0, 10);
      const c = byDate.get(ds) || 0;
      cells.push(
        <rect key={`${w_i}-${d_i}`}
              x={w_i * (cellSize + gap)} y={d_i * (cellSize + gap)}
              width={cellSize} height={cellSize} fill={shade(c)} rx={2}>
          <title>{`${ds}: ${c}`}</title>
        </rect>
      );
    }
  }
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMinYMin meet"
         style={{ width: "100%", height: h, display: "block" }}>
      {cells}
    </svg>
  );
}

// entry: { user, commits, max, activeDays, daily: [{date, count}] }
export function ContributorCard({ entry, color, view, onOpen, unit = "commits" }) {
  const u = entry.user || {};
  const name = `${u.name || ""} ${u.lastName || ""}`.trim() || "Contributor";
  const role = u.role || "";
  return (
    <div className="ant-card ant-card-bordered" style={{ padding: 12, background: "#fff" }}>
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: 8, cursor: onOpen ? "pointer" : "default" }}
        title="Open this user's analytics"
        onClick={onOpen}
      >
        <div style={{
          width: 32, height: 32, borderRadius: "50%",
          background: color, color: "#fff", marginRight: 8,
          display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600,
        }}>{name.charAt(0).toUpperCase()}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
          <div style={{ fontSize: 12, color: "#888" }}>
            {entry.commits} {unit} · max {entry.max} · {entry.activeDays} active days
          </div>
        </div>
        {role && (
          <span className="ant-tag" style={{ marginRight: 0 }}>{role}</span>
        )}
      </div>
      {view === "heatmap"
        ? <Heatmap daily={entry.daily} color={color} maxValue={entry.max} />
        : <BarHistogram daily={entry.daily} color={color} maxValue={entry.max} />}
    </div>
  );
}

export function TeamStackedBars({ byUser, unit = "commits" }) {
  const dateSet = new Set();
  byUser.forEach(u => u.daily.forEach(d => { if (d.count) dateSet.add(d.date); }));
  const dates = Array.from(dateSet).sort();
  if (dates.length === 0) return <Empty description={`No ${unit} in window`} />;
  let globalMax = 1;
  for (const d of dates) {
    let stack = 0;
    for (const u of byUser) {
      const found = u.daily.find(x => x.date === d);
      if (found) stack += found.count;
    }
    if (stack > globalMax) globalMax = stack;
  }
  return (
    <svg viewBox={`0 0 ${dates.length} ${globalMax}`} preserveAspectRatio="none"
         style={{ width: "100%", height: 120, display: "block" }}>
      {dates.map((dt, i) => {
        let cursorY = globalMax;
        return byUser.map((u, ui) => {
          const cell = u.daily.find(x => x.date === dt);
          if (!cell || !cell.count) return null;
          cursorY -= cell.count;
          return (
            <rect key={`${i}-${ui}`} x={i + 0.1} y={cursorY}
                  width={0.8} height={cell.count} fill={colorFor(ui)}>
              <title>{`${(u.user?.name || "")} ${dt}: ${cell.count}`}</title>
            </rect>
          );
        });
      })}
    </svg>
  );
}
